// src/db/fileManagerDB.js
// ✅ Pure I/O Layer - IndexedDB 직접 조작만 담당
// 🔧 수정: 커서 패턴 버그 수정, idb v7+ 호환, 문서 파일 지원 추가

import { openDB, deleteDB } from 'idb';
import { logError, logInfo, logWarn, logDebug } from '@/utils/logHelper';
import { generateUUID } from '@/utils/uuid';
/* ═══════════════════════════════════════════════════════════
 * Constants
 * ═══════════════════════════════════════════════════════════ */
const DB_NAME = 'fileManagerDB';
const DB_VERSION = 2;
const STORE_FILES = 'files';
const STORE_DOCUMENTS = 'documents';  // ✅ 수정: STORE_DOC → STORE_DOCUMENTS
const STORE_TICKETS = 'reviewTickets';

let dbPromise = null;

/* ═══════════════════════════════════════════════════════════
 * DB Connection Management
 * ═══════════════════════════════════════════════════════════ */

/**
 * IndexedDB 연결 획득 (싱글톤)
 * @returns {Promise<IDBDatabase>}
 */
export async function getDB() {
  if (dbPromise) return dbPromise;

  logDebug(`[fileManagerDB] Opening '${DB_NAME}' v${DB_VERSION}...`);

  dbPromise = openDB(DB_NAME, DB_VERSION, {
    async upgrade(db, oldVersion, newVersion, tx) {
      logInfo(`[fileManagerDB.upgrade] v${oldVersion} → v${newVersion}`);

      // ✅ files 스토어 (도면 파일)
      if (!db.objectStoreNames.contains(STORE_FILES)) {
        const fileStore = db.createObjectStore(STORE_FILES, {
          keyPath: 'id',
          autoIncrement: true,
        });
        fileStore.createIndex('by_name', 'name');
        fileStore.createIndex('by_status', 'status');
        fileStore.createIndex('by_uuid', 'uuid');
        logInfo('[fileManagerDB.upgrade] ✅ files store created');
      }

      // ✅ documents 스토어 (문서 파일)
      if (!db.objectStoreNames.contains(STORE_DOCUMENTS)) {
        const documentsStore = db.createObjectStore(STORE_DOCUMENTS, {
          keyPath: 'uuid',
          autoIncrement: false,
        });
        documentsStore.createIndex('by_name', 'name', { unique: false });
        documentsStore.createIndex('by_type', 'mimeType', { unique: false });
        documentsStore.createIndex('by_uploadDate', 'uploadedAt', { unique: false });
        documentsStore.createIndex('by_uuid', 'uuid', { unique: true });
        logInfo('[fileManagerDB.upgrade] ✅ documents store created');
      }

      // ✅ reviewTickets 스토어
      if (!db.objectStoreNames.contains(STORE_TICKETS)) {
        const ticketStore = db.createObjectStore(STORE_TICKETS, {
          keyPath: 'id',
          autoIncrement: true,
        });
        ticketStore.createIndex('by_fileUuid', 'fileUuid', { unique: false });
        ticketStore.createIndex('by_status', 'status', { unique: false });
        ticketStore.createIndex('by_annotationId', 'annotationId', { unique: false });
        logInfo('[fileManagerDB.upgrade] ✅ reviewTickets store created');
      }

      // ✅ v1 → v2 마이그레이션: status 필드 추가
      if (oldVersion < 2 && db.objectStoreNames.contains(STORE_FILES)) {
        try {
          const fileStore = tx.objectStore(STORE_FILES);
          const allRecords = await fileStore.getAll();
          
          for (const record of allRecords) {
            if (!record.status) {
              record.status = 'completed';
              await fileStore.put(record);
            }
          }
          
          logInfo('[fileManagerDB.upgrade] ✅ Migration v1→v2 completed');
        } catch (err) {
          logError('[fileManagerDB.upgrade] Migration failed:', err);
        }
      }
    },

    blocked() {
      logWarn('[fileManagerDB] DB 업그레이드 차단됨 - 다른 탭에서 사용 중');
    },

    blocking() {
      logWarn('[fileManagerDB] 이 탭이 다른 탭의 업그레이드를 차단 중');
      dbPromise?.then(db => db.close()).catch(() => {});
      dbPromise = null;
    },

    terminated() {
      logWarn('[fileManagerDB] DB 연결이 예기치 않게 종료됨');
      dbPromise = null;
    },
  });

  return dbPromise;
}

/**
 * DB 연결 닫기
 */
export async function closeDB() {
  if (!dbPromise) return;
  
  try {
    const db = await dbPromise;
    db.close();
  } catch (e) {
    logWarn('[fileManagerDB.closeDB] Error:', e);
  } finally {
    dbPromise = null;
  }
}

/**
 * DB 삭제 및 재생성
 * ⚠️ 모든 데이터 삭제됨
 */
export async function resetDatabase() {
  logWarn('[fileManagerDB.resetDatabase] DB 삭제 시작...');
  
  await closeDB();
  await new Promise(resolve => setTimeout(resolve, 100));
  await deleteDB(DB_NAME);
  
  logInfo('[fileManagerDB.resetDatabase] DB 삭제 완료');
  return getDB();
}

/* ═══════════════════════════════════════════════════════════
 * Drawing Files CRUD (Pure I/O)
 * ═══════════════════════════════════════════════════════════ */

/**
 * 파일 목록 조회 (페이지네이션)
 * @param {Object} options - { limit, offset }
 * @returns {Promise<Array>} 원본 레코드 배열
 */
export async function getAllPaged({ limit = 100, offset = 0 } = {}) {
  const db = await getDB();
  const tx = db.transaction(STORE_FILES, 'readonly');
  const store = tx.objectStore(STORE_FILES);
  
  const results = [];
  let idx = 0;

  let cursor = await store.openCursor();
  
  while (cursor) {
    if (idx >= offset) {
      results.push(cursor.value);
      if (results.length >= limit) break;
    }
    idx++;
    
    try {
      cursor = await cursor.continue();
    } catch {
      break;
    }
  }

  await tx.done;
  return results;
}

/**
 * 더 안전한 페이지네이션 (getAll 기반)
 * 대용량 데이터에서는 커서보다 효율적
 */
export async function getAllPagedSafe({ limit = 100, offset = 0 } = {}) {
  const db = await getDB();
  const all = await db.getAll(STORE_FILES);
  return all.slice(offset, offset + limit);
}

/**
 * 모든 도면 파일 조회
 * @returns {Promise<Array>}
 */
export async function getAllFiles() {
  const db = await getDB();
  return db.getAll(STORE_FILES);
}

/**
 * 단일 도면 파일 조회
 * @param {number} id
 * @returns {Promise<Object|undefined>}
 */
export async function getFile(id) {
  const db = await getDB();
  return db.get(STORE_FILES, id);
}

/**
 * 도면 파일 스텁 추가 (메타데이터만, 변환 전)
 * @param {Object} stub - { name, size, type, status, ... }
 * @returns {Promise<number>} 생성된 ID
 */
export async function addFileStub(stub) {
  const db = await getDB();
  const tx = db.transaction(STORE_FILES, 'readwrite');
  
  const now = new Date().toISOString();
  const record = {
    ...stub,
    createdAt: now,
    updatedAt: now,
  };
  
  const id = await tx.store.add(record);
  await tx.done;
  
  logDebug(`[fileManagerDB.addFileStub] ID=${id}`, record.name);
  return id;
}

/**
 * 도면 파일 부분 업데이트
 * @param {number} id
 * @param {Object} patch - 업데이트할 필드들
 * @param {Object} options - { silentIfMissing: boolean }
 * @returns {Promise<Object|null>} 업데이트된 레코드 또는 null
 */
export async function patchFile(id, patch, { silentIfMissing = false } = {}) {
  const db = await getDB();
  const tx = db.transaction(STORE_FILES, 'readwrite');
  
  const existing = await tx.store.get(id);
  
  if (!existing) {
    await tx.done;
    if (silentIfMissing) {
      logDebug(`[fileManagerDB.patchFile] ID=${id} not found (silent)`);
      return null;
    }
    throw new Error(`File not found: ID=${id}`);
  }

  const updated = {
    ...existing,
    ...patch,
    id,
    updatedAt: new Date().toISOString(),
  };

  await tx.store.put(updated);
  await tx.done;

  logDebug(`[fileManagerDB.patchFile] ID=${id} updated`);
  return updated;
}

/**
 * 도면 파일 삭제
 * @param {number} id
 */
export async function deleteFile(id) {
  const db = await getDB();
  await db.delete(STORE_FILES, id);
  logDebug(`[fileManagerDB.deleteFile] ID=${id} deleted`);
}

/**
 * 상태별 도면 파일 조회
 * @param {string} status - 'queued' | 'processing' | 'completed' | 'error'
 * @returns {Promise<Array>}
 */
export async function getFilesByStatus(status) {
  const db = await getDB();
  const tx = db.transaction(STORE_FILES, 'readonly');
  const index = tx.store.index('by_status');
  return index.getAll(status);
}

/* ═══════════════════════════════════════════════════════════
 * Document Files CRUD (Pure I/O)
 * ═══════════════════════════════════════════════════════════ */

/**
 * 문서 파일 추가
 * @param {Object} docData - { uuid, name, size, mimeType, ... }
 * @returns {Promise<string>} uuid
 */
/**
 * 문서 파일 추가
 * @param {Object} docData - { uuid, name, size, mimeType, ... }
 * @returns {Promise<string>} uuid
 */
export async function addDocument(docData) {
  const db = await getDB();
  
  // ✅ uuid가 없으면 생성
  const uuid = docData.uuid || generateUUID();
  
  const now = new Date().toISOString();
  const record = {
    ...docData,
    uuid, // ✅ 명시적으로 uuid 포함
    createdAt: docData.createdAt || now,
    uploadedAt: docData.uploadedAt || now,
    updatedAt: now,
  };
  
  try {
    const tx = db.transaction(STORE_DOCUMENTS, 'readwrite');
    // ✅ add()는 키를 반환하므로 await 필요
    await tx.store.add(record);
    await tx.done;
    
    logDebug(`[fileManagerDB.addDocument] uuid=${uuid}`, record.name);
    return uuid; // ✅ 생성/전달받은 uuid 반환
  } catch (error) {
    // ✅ 중복 키 에러 등 상세 로깅
    logError('[fileManagerDB.addDocument] 추가 실패', {
      uuid,
      name: record.name,
      error: error.message,
    });
    throw error;
  }
}

/**
 * 모든 문서 파일 조회
 * @returns {Promise<Array>}
 */
export async function getDocuments() {
  const db = await getDB();
  return db.getAll(STORE_DOCUMENTS);
}

/**
 * 단일 문서 파일 조회
 * @param {string} uuid
 * @returns {Promise<Object|undefined>}
 */
export async function getDocument(uuid) {
  const db = await getDB();
  return db.get(STORE_DOCUMENTS, uuid);
}

/**
 * 문서 파일 삭제
 * @param {string} uuid
 */
export async function deleteDocument(uuid) {
  const db = await getDB();
  await db.delete(STORE_DOCUMENTS, uuid);
  logDebug(`[fileManagerDB.deleteDocument] uuid=${uuid} deleted`);
}

/**
 * MIME 타입별 문서 조회
 * @param {string} mimeType - 'application/pdf', 'image/png' 등
 * @returns {Promise<Array>}
 */
export async function getDocumentsByType(mimeType) {
  const db = await getDB();
  const tx = db.transaction(STORE_DOCUMENTS, 'readonly');
  const index = tx.store.index('by_type');
  return index.getAll(mimeType);
}

/**
 * 문서 파일 부분 업데이트
 * @param {string} uuid
 * @param {Object} patch - 업데이트할 필드들
 * @returns {Promise<Object|null>}
 */
export async function patchDocument(uuid, patch) {
  const db = await getDB();
  const tx = db.transaction(STORE_DOCUMENTS, 'readwrite');
  
  const existing = await tx.store.get(uuid);
  
  if (!existing) {
    await tx.done;
    logDebug(`[fileManagerDB.patchDocument] uuid=${uuid} not found`);
    return null;
  }

  const updated = {
    ...existing,
    ...patch,
    uuid,
    updatedAt: new Date().toISOString(),
  };

  await tx.store.put(updated);
  await tx.done;

  logDebug(`[fileManagerDB.patchDocument] uuid=${uuid} updated`);
  return updated;
}

/**
 * 업로드 날짜별 문서 조회
 * @param {string} startDate - ISO 날짜 문자열
 * @param {string} endDate - ISO 날짜 문자열
 * @returns {Promise<Array>}
 */
export async function getDocumentsByDateRange(startDate, endDate) {
  const db = await getDB();
  const tx = db.transaction(STORE_DOCUMENTS, 'readonly');
  const index = tx.store.index('by_uploadDate');
  
  const range = IDBKeyRange.bound(startDate, endDate);
  return index.getAll(range);
}

/* ═══════════════════════════════════════════════════════════
 * ReviewTickets CRUD (Pure I/O)
 * ═══════════════════════════════════════════════════════════ */

/**
 * 리뷰 티켓 추가
 * @param {Object} ticketData
 * @returns {Promise<number>} 생성된 ID
 */
export async function addReviewTicket(ticketData) {
  const db = await getDB();
  
  if (!db.objectStoreNames.contains(STORE_TICKETS)) {
    throw new Error('reviewTickets store not found. DB reset required.');
  }

  const tx = db.transaction(STORE_TICKETS, 'readwrite');
  const now = new Date().toISOString();

  const record = {
    status: 'OPEN',
    createdAt: now,
    updatedAt: now,
    ...ticketData,
  };

  const id = await tx.store.add(record);
  await tx.done;

  logDebug(`[fileManagerDB.addReviewTicket] ID=${id}`);
  return id;
}

/**
 * 파일 UUID로 리뷰 티켓 조회
 * @param {string} fileUuid
 * @returns {Promise<Array>}
 */
export async function getReviewTicketsByFileUuid(fileUuid) {
  const db = await getDB();
  const tx = db.transaction(STORE_TICKETS, 'readonly');
  const index = tx.store.index('by_fileUuid');
  return index.getAll(fileUuid);
}

/**
 * 리뷰 티켓 업데이트
 * @param {number} id
 * @param {Object} patch
 * @returns {Promise<Object|null>}
 */
export async function patchReviewTicket(id, patch) {
  const db = await getDB();
  const tx = db.transaction(STORE_TICKETS, 'readwrite');
  
  const existing = await tx.store.get(id);
  if (!existing) {
    await tx.done;
    return null;
  }

  const updated = {
    ...existing,
    ...patch,
    id,
    updatedAt: new Date().toISOString(),
  };

  await tx.store.put(updated);
  await tx.done;

  return updated;
}

/**
 * 리뷰 티켓 삭제
 * @param {number} id
 */
export async function deleteReviewTicket(id) {
  const db = await getDB();
  await db.delete(STORE_TICKETS, id);
}

/**
 * 파일 UUID에 해당하는 모든 리뷰 티켓 삭제
 * @param {string} fileUuid
 * @returns {Promise<number>} 삭제된 개수
 */
export async function deleteReviewTicketsByFileUuid(fileUuid) {
  const db = await getDB();
  const tx = db.transaction(STORE_TICKETS, 'readwrite');
  const index = tx.store.index('by_fileUuid');
  
  const tickets = await index.getAll(fileUuid);
  
  await Promise.all(tickets.map(ticket => tx.store.delete(ticket.id)));
  
  await tx.done;
  logDebug(`[fileManagerDB.deleteReviewTicketsByFileUuid] Deleted ${tickets.length} tickets`);
  
  return tickets.length;
}

/* ═══════════════════════════════════════════════════════════
 * Utility Functions
 * ═══════════════════════════════════════════════════════════ */

/**
 * 전체 통계 조회 (도면 + 문서)
 * @returns {Promise<Object>} { drawingCount, documentCount, totalSize }
 */
export async function getDatabaseStats() {
  const db = await getDB();
  
  const [drawings, documents] = await Promise.all([
    db.getAll(STORE_FILES),
    db.getAll(STORE_DOCUMENTS),
  ]);
  
  const totalSize = [
    ...drawings.map(f => f.size || 0),
    ...documents.map(d => d.size || 0),
  ].reduce((sum, size) => sum + size, 0);
  
  return {
    drawingCount: drawings.length,
    documentCount: documents.length,
    totalCount: drawings.length + documents.length,
    totalSize,
  };
}

/**
 * 특정 테이블의 모든 데이터 삭제
 * @param {string} storeName - 'files' | 'documents' | 'reviewTickets'
 */
export async function clearStore(storeName) {
  const db = await getDB();
  const tx = db.transaction(storeName, 'readwrite');
  await tx.store.clear();
  await tx.done;
  logInfo(`[fileManagerDB.clearStore] ${storeName} cleared`);
}